import React, { Component } from 'react';
import useLog from '@/hooks/useLog';
import { trackEvent, captureException } from '@/analytics';
import {
  claimChunkAlert,
  claimChunkRecovery,
  isChunkLoadError,
  isChunkSafeMode,
  recentPreloadError,
  recoverFromChunkError
} from './chunkPreloadError';

interface WithLoggingProps {
  logAndNotify: (message: string, error?: any) => void;
  children: React.ReactNode;
}

const withLogging = <P extends WithLoggingProps>(WrappedComponent: React.ComponentType<P>) => {
  return function WithLoggingComponent(props: Omit<P, keyof WithLoggingProps>) {
    const { logAndNotify } = useLog();

    return <WrappedComponent {...(props as P)} logAndNotify={logAndNotify} />;
  };
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  logAndNotify: (message: string, error?: any) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  secondsLeft: number;
  isChunkError: boolean;
  isReloading: boolean;
}

const getChunkTelemetry = (error: Error) => {
  const message = error?.message || '';
  const swallowedPreload = recentPreloadError();

  return {
    route: window.location.pathname,
    error_name: error?.name || 'Error',
    // El TypeError que deja un preload cancelado no nombra el asset: sale del
    // mensaje del preloadError que quedó registrado.
    chunk_asset:
      message.match(/\/assets\/([^?\s]+\.js)/i)?.[1] ||
      swallowedPreload?.message.match(/\/assets\/([^?\s]+\.js)/i)?.[1],
    preload_swallowed: !!swallowedPreload,
    // El error REAL del import() que se tragó el preventDefault. El TypeError que llega
    // al boundary («evaluating 'e.default'») es su consecuencia y no dice nada: en Safari
    // un chunk que no bajó deja «Importing a module script failed.» (sin URL, por eso
    // chunk_asset sale vacío), y un módulo que tiró al evaluarse deja SU error. Sin esto
    // no hay forma de separar red de bug (las pantallas en loop de iPhone del 6/10).
    preload_error: swallowedPreload?.message.slice(0, 200),
    deployment_id: import.meta.env.VITE_DEPLOYMENT_ID
  };
};

class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private redirectTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      secondsLeft: 3,
      isChunkError: false,
      isReloading: false
    };
  }

  formatError(error: unknown, errorInfo: React.ErrorInfo): string {
    try {
      let errorMessage: string;

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        if ((error as any).message) {
          errorMessage = (error as any).message;
        } else {
          try {
            errorMessage = JSON.stringify(error).substring(0, 500);
          } catch {
            errorMessage = 'Non-serializable error object';
          }
        }
      } else {
        errorMessage = 'An unknown error occurred';
      }

      const componentStack = errorInfo?.componentStack
        ? `\nComponent Stack: ${errorInfo.componentStack}`
        : '';

      return `${errorMessage}${componentStack}`;
    } catch {
      return 'Error while formatting error message';
    }
  }

  isChunkLoadError(error: Error): boolean {
    return isChunkLoadError(error);
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const chunkError = this.isChunkLoadError(error);

    // Chunk load errors mean the bundle the user has is stale relative to
    // what's on the CDN (expected churn after a deploy, NOT an incident).
    // Recover silently (reload, then safe mode). Only the terminal case surfaces
    // an error UI, and it notifies Slack once per tab so #incidents isn't flooded.
    if (chunkError) {
      // Escalera (ver chunkPreloadError.ts): 1) reload con URL nueva; 2) si vuelve a
      // fallar enseguida, modo seguro (sin Service Worker ni cachés ni la ráfaga de
      // modulepreload) y reload; 3) recién ahí la pantalla. Nunca loop: cada paso queda
      // contado en sessionStorage.
      const { step, attempt } = claimChunkRecovery();

      // Chunk error = bundle viejo vs CDN tras un deploy, o una red que cortó el
      // chunk. NO es incidente mientras la escalera lo recupere: va a PostHog, no a Slack.
      const swControlled = typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
      const chunkTelemetry = {
        ...getChunkTelemetry(error),
        attempt,
        // true = esta pestaña ya está sin SW ni modulepreload (pasó por el paso 2).
        safe_mode: isChunkSafeMode(),
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        // Cuánto hacía que la página había cargado: separa el fallo en el arranque del que
        // llega navegando dentro de la app.
        since_load_ms: typeof performance !== 'undefined' ? Math.round(performance.now()) : undefined
      };
      if (step !== 'screen') {
        // Métrica de la "cola": recuperación silenciosa ('auto_reload') y, si el reload
        // no alcanzó, la del modo seguro ('safe_reload').
        try {
          trackEvent('chunk_recovery', {
            screen: 'error_boundary',
            outcome: step === 'reload' ? 'auto_reload' : 'safe_reload',
            sw_controlled: swControlled,
            ...chunkTelemetry
          });
        } catch {
          /* el tracking nunca bloquea la recuperación */
        }
        // Estado neutro + recuperación casi inmediata.
        this.setState({ isReloading: true });
        this.redirectTimer = setTimeout(() => void recoverFromChunkError(step), 150);
        return;
      }

      // Caso terminal: ni el reload ni el modo seguro lo recuperaron. Lo medimos por DOS vías:
      //  - PostHog: outcome 'screen_shown', CADA vez (entra en la misma curva que la cola).
      //  - Slack: UNA alerta por pestaña (claimChunkAlert), por más que el usuario apriete
      //    «Actualizar ahora» varias veces. El tag arranca con [CHUNK] para pasar el filtro
      //    de cyclone; "PANTALLA" lo distingue de la recuperación silenciosa.
      // sw=off con modo=seguro acá descarta al SW: lo que traba es la red o WebKit.
      try {
        trackEvent('chunk_recovery', {
          screen: 'error_boundary',
          outcome: 'screen_shown',
          sw_controlled: swControlled,
          ...chunkTelemetry
        });
      } catch {
        /* el tracking nunca bloquea la recuperación */
      }
      if (claimChunkAlert()) {
        try {
          this.props.logAndNotify(
            `[CHUNK] PANTALLA (sw=${swControlled ? 'on' : 'off'}, modo=${chunkTelemetry.safe_mode ? 'seguro' : 'normal'}, intento ${attempt}) ` +
              `${chunkTelemetry.safe_mode ? 'ni el reload ni el modo seguro recuperaron' : 'el reload no recuperó'} - ${error?.message || 'sin mensaje'}` +
              (chunkTelemetry.preload_error ? ` - import: ${chunkTelemetry.preload_error}` : '')
          );
        } catch {
          // nunca dejar que el logging rompa la recuperación
        }
      }
      this.setState({ hasError: true, error, errorInfo, isChunkError: true });
      return;
    }

    this.setState({
      hasError: true,
      error,
      errorInfo,
      isChunkError: chunkError
    });

    // For all other errors, keep the legacy behavior: redirect to / after a
    // short countdown so the user isn't stuck on a broken screen.
    this.countdownTimer = setInterval(() => {
      this.setState((prev) => ({
        secondsLeft: prev.secondsLeft - 1
      }));
    }, 1000);

    this.redirectTimer = setTimeout(() => {
      window.location.href = '/';
    }, 3000);

    try {
      const formattedError = this.formatError(error, errorInfo);
      this.props.logAndNotify(formattedError);
    } catch (loggingError) {
      console.error('Error while trying to log:', loggingError);
    }

    // Send to PostHog error tracking (groups by issue + links to the session
    // replay when recording). Only real React errors — chunk errors return early
    // above and are treated as expected post-deploy recovery, not incidents.
    try {
      captureException(error, {
        source: 'react_error_boundary',
        component_stack: errorInfo.componentStack
      });
    } catch {
      /* error tracking must never block recovery */
    }
  }

  componentWillUnmount() {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  render() {
    if (this.state.isReloading) {
      return (
        <div className="min-h-screen bg-frost-paper flex flex-col justify-center items-center px-4">
          <p className="text-sm text-frost-ink-soft">Actualizando a la última versión…</p>
        </div>
      );
    }

    if (this.state.hasError && this.state.isChunkError) {
      return (
        <div className="min-h-screen bg-frost-paper flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6 bg-frost-card p-8 sm:p-10 rounded-xl shadow-md text-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-frost-ink">Hay una versión nueva</h2>
              <p className="mt-3 text-sm text-frost-ink-soft">
                Esta sección necesita el último código de Ágora para funcionar. Actualizá la página para
                continuar — tu trabajo guardado se mantiene.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // El usuario pide otro intento: va directo al modo seguro (el reload común
                // ya falló). Si vuelve a fallar reaparece esta pantalla, sin otra alerta.
                this.setState({ isReloading: true });
                void recoverFromChunkError('safe_reload');
              }}
              className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-fs-13-5 font-semibold border border-frost-card/60 bg-[rgba(var(--f-card-rgb),0.78)] hover:bg-[rgba(var(--f-card-rgb),0.95)] transition-colors"
              style={{ color: '#0050B3' }}
            >
              Actualizar ahora
            </button>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-frost-paper flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 bg-frost-card p-10 rounded-xl shadow-md text-center">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-frost-ink">
                ¡Ups! Algo salió mal
              </h2>
              <p className="mt-2 text-center text-sm text-frost-ink-soft">
                Hemos sido notificados y estamos trabajando en una solución.
              </p>
            </div>
            <div className="mt-8 space-y-6">
              <div className="rounded-md bg-frost-card border border-frost-warning/30 p-4">
                <div className="flex">
                  <div className="shrink-0">
                    <svg
                      className="h-5 w-5 text-frost-warning"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-frost-ink">Redirigiendo a la página de inicio</h3>
                    <div className="mt-2 text-sm text-frost-ink-soft">
                      <p>Serás redirigido en {this.state.secondsLeft} segundos...</p>
                    </div>
                  </div>
                </div>
              </div>
              {import.meta.env.VITE_NODE_ENV === 'development' && (
                <div className="mt-4 text-left">
                  <details className="bg-frost-paper p-4 rounded-lg">
                    <summary className="text-frost-ink-soft cursor-pointer font-medium">
                      Error Details
                    </summary>
                    <pre className="mt-2 text-sm text-frost-ink-soft overflow-auto whitespace-pre-wrap">
                      {this.state.error && this.state.error.toString()}
                      {this.state.errorInfo && (
                        <>
                          <br />
                          <br />
                          Component Stack:
                          <br />
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = withLogging(ErrorBoundaryComponent);
